import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Leaf, Snowflake, Heart, Sparkles } from 'lucide-react';
import Bottle from '@/components/ui/Bottle';
import { PRODUCTS } from '@/data/products';

export default function ProductDetail() {
  const { id } = useParams();
  const product = PRODUCTS.find((p) => p.id === id);
  if (!product) {
    return (
      <div className="container-x py-32 text-center" data-testid="page-product-not-found">
        <h1 className="font-display text-4xl text-bone-50">Bottle not found</h1>
        <Link to="/shop" className="btn-primary mt-6 inline-flex">Back to shop</Link>
      </div>
    );
  }
  const others = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 4);

  return (
    <div data-testid="page-product-detail">
      <section className="container-x pt-10 pb-4">
        <Link to="/shop" className="inline-flex items-center gap-2 text-bone-300/80 hover:text-gold text-xs tracking-[0.22em] uppercase">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to shop
        </Link>
      </section>

      <section className="container-x pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 bottle-stage rounded-3xl p-10 lg:p-16 grain relative">
          <div className="absolute inset-0 bg-radial-spot pointer-events-none" />
          <div className="flex justify-center"><Bottle product={product} size="xl" floating /></div>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <span className="eyebrow">{product.category.replace('-', ' ')}</span>
          <h1 className="font-display text-5xl lg:text-6xl text-bone-50 leading-[1.02]">{product.name}</h1>
          <p className="font-display italic text-2xl text-sage-300">{product.tagline}</p>
          <p className="text-bone-200/90 leading-relaxed">{product.description}</p>
          <div className="flex items-center gap-5">
            <span className="font-display text-4xl text-gold">₹{product.price}</span>
            <span className="text-bone-300 text-sm">{product.volume}</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="btn-primary" data-testid={`detail-add-${product.id}`}><ShoppingBag className="w-4 h-4" /> Add to cart</button>
            <Link to="/shop" className="btn-outline">Continue shopping</Link>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Tag icon={<Leaf className="w-4 h-4" />} label="Real Ingredients" />
            <Tag icon={<Sparkles className="w-4 h-4" />} label="No Added Sugar" />
            <Tag icon={<Snowflake className="w-4 h-4" />} label="Refrigerated" />
            <Tag icon={<Heart className="w-4 h-4" />} label="Made With Love" />
          </div>
        </div>
      </section>

      <section className="container-x pb-24">
        <h3 className="font-display text-3xl text-bone-50 mb-6">You may also love</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {others.map((p) => (
            <Link
              key={p.id}
              to={`/shop/${p.id}`}
              className="glass-card rounded-2xl p-5 hover:border-gold/30 transition-all flex flex-col items-center text-center"
            >
              <div className="bottle-stage rounded-2xl w-full py-6 flex justify-center mb-4">
                <Bottle product={p} size="sm" />
              </div>
              <p className="font-display text-lg text-bone-50">{p.name}</p>
              <p className="text-gold text-sm mt-1">₹{p.price}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

const Tag = ({ icon, label }) => (
  <div className="glass-soft rounded-full px-4 py-2.5 flex items-center gap-3 text-sm text-bone-100">
    <span className="w-7 h-7 rounded-full border border-gold/30 flex items-center justify-center text-gold">{icon}</span>
    {label}
  </div>
);
